import React from "react";
import { Typography, Grid, Paper } from "@material-ui/core";
import { useStyles } from "./style";
import moment from "moment";
import { Tooltip } from "@mui/material";

interface Props {
  details: any;
  nomination: any;
  vehicleStatus?: any;
  isExportMode?: boolean;
  effectiveDate?: Date;
  agent?: any;
}

export function TerminalDetails({
  details,
  nomination,
  vehicleStatus = {},
  isExportMode = false,
  effectiveDate,
  agent,
}: Props): React.ReactElement {
  const classes = useStyles();
  const terminalType = Number(localStorage.getItem("terminalType"));
  const invoiceTypeUpper = String(details?.invoiceType || "").toUpperCase();
  const isBydInvoice = invoiceTypeUpper === "BYD";
  const isPartywiseInvoice = invoiceTypeUpper === "PARTYWISE";
  const isBillGroupedLayout = isBydInvoice || isPartywiseInvoice;
  const bydSelectedIndexNumber = (() => {
    const raw =
      (details as any)?.indexNumber ?? (details as any)?.IndexNo ?? (details as any)?.indexNo ?? "";
    const v = String(raw ?? "").trim();
    return v.length ? v : null;
  })();
  const destuffDateStr = details?.destuffingDate;
  const gateinDate = details?.gateInDate || details?.createdDateTime;
  let storageDays = 0;
  const baseDate =
    effectiveDate instanceof Date && !Number.isNaN(effectiveDate.getTime())
      ? effectiveDate
      : new Date();
  if (details?.containerType === "CY") {
    if (gateinDate) {
      const createdDate = new Date(gateinDate);
      const today = baseDate;

      // Remove the +5 grace days
      const diffTime = today.getTime() - createdDate.getTime();

      storageDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    }
  } else {
    if (destuffDateStr) {
      const createdDate = new Date(destuffDateStr);
      const today = baseDate;

      // Remove the +5 grace days
      const diffTime = today.getTime() - createdDate.getTime();

      storageDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    }
  }

  function truncateString(value: unknown, maxLength = 8) {
    const str = value == null ? "" : String(value);
    if (str.length > maxLength) return `${str.slice(0, maxLength)}...`;
    return str;
  }

  // Toggle headings based on mode so the relevant section is always visible.
  // Export: always show the vehicle status headings (values fill once a vehicle is selected).
  // Import/others: always show container details headings; vehicle status shows only when fetched.
  const hasVehicleStatus = vehicleStatus && Object.keys(vehicleStatus).length > 0;
  const showVehicleStatus = isExportMode || hasVehicleStatus;
  const showContainerDetails = !isExportMode;
  const forwarderLabel =
    details?.forwarderName ||
    details?.forwarderDisplayName ||
    details?.partyName ||
    details?.forwarder ||
    "-";
  const bydContainerCountLabel = details?.bydContainerCountLabel || "-";
  const bydCargoTypesLabel = details?.bydCargoTypesLabel || details?.cargoType || "-";
  const cargoTypeLabel = (() => {
    const raw =
      details?.cargoTypeName ??
      details?.cargoType ??
      vehicleStatus?.cargoTypeName ??
      vehicleStatus?.cargoType ??
      "-";
    const v = String(raw ?? "").trim();
    return v.length ? v : "-";
  })();
  const agentNameLabel = (() => {
    const raw = agent?.name ?? (details as any)?.agentName ?? "";
    const v = String(raw ?? "").trim();
    return v.length ? v : "-";
  })();
  const agentCnicLabel = (() => {
    const raw = agent?.cnic ?? (details as any)?.agentCnic ?? (details as any)?.cnic ?? "";
    const v = String(raw ?? "").trim();
    return v.length ? v : "-";
  })();
  const agentPhoneLabel = (() => {
    const raw =
      agent?.phoneNumber ?? (details as any)?.agentPhone ?? (details as any)?.phoneNumber ?? "";
    const v = String(raw ?? "").trim();
    return v.length ? v : "-";
  })();
  const agentRepNameLabel = (() => {
    const raw = agent?.repName ?? (details as any)?.agentRepName ?? (details as any)?.repName ?? "";
    const v = String(raw ?? "").trim();
    return v.length ? v : "-";
  })();
  const containerTypeLabel = (() => {
    const normalize = (v: unknown) => String(v ?? "").trim();
    const allowedGateInTypes = new Set(["LOOSE CARGO", "BONDED CARGO"]);

    // terminalType === 4: ONLY show the Gate-In dropdown value (LOOSE/BONDED),
    // not the cargo type name (e.g. "Auto Parts").
    if (terminalType === 4) {
      const candidates = [
        vehicleStatus?.cargoType,
        vehicleStatus?.containerType,
        details?.cargoType,
        details?.containerType,
      ]
        .map(normalize)
        .filter(Boolean);

      const match = candidates.find((v) => allowedGateInTypes.has(v.toUpperCase()));
      return match || "-";
    }

    // Other terminals: keep existing behavior.
    const raw =
      details?.containerType ??
      vehicleStatus?.containerType ??
      details?.container_type ??
      details?.containerTypeName ??
      "";
    const v = normalize(raw);
    return v.length ? v : "-";
  })();
  const partywiseStorageDays = (details as any)?.partywiseStorageDays;
  const partywiseBaseStorageDays = (details as any)?.partywiseBaseStorageDays;
  const partywiseContainerCount = (details as any)?.partywiseContainerCount;
  const partywiseSizeBreakdown = (details as any)?.partywiseSizeBreakdown;
  const displayPartywiseStorageDays =
    partywiseStorageDays != null ? partywiseStorageDays : storageDays;
  const partywiseStorageTooltip = (() => {
    if (partywiseBaseStorageDays == null || !partywiseContainerCount) {
      return String(displayPartywiseStorageDays || 0);
    }
    const sizePart = partywiseSizeBreakdown
      ? ` · ${partywiseSizeBreakdown}`
      : ` · ${partywiseContainerCount} container${partywiseContainerCount === 1 ? "" : "s"}`;
    return `${displayPartywiseStorageDays} days from latest gate-in (CY slab per container${sizePart})`;
  })();

  const virNumberLabel = (() => {
    const raw =
      (details as any)?.virNumber ?? (details as any)?.virNo ?? (details as any)?.vir ?? "";
    const v = String(raw ?? "").trim();
    return v.length ? v : "-";
  })();

  const containerNumbersLabel = (() => {
    const raw = (details as any)?.containerNumbersLabel ?? (details as any)?.containerNumber ?? "";
    if (Array.isArray(raw)) {
      const joined = raw
        .map((x) => String(x ?? "").trim())
        .filter(Boolean)
        .join(", ");
      return joined.length ? joined : "-";
    }
    const v = String(raw ?? "").trim();
    return v.length ? v : "-";
  })();

  const renderVirAndContainerFields = () => (
    <>
      <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
        <Grid item xs={6} md={6} lg={6}>
          <Typography className={classes.readOnyTitle}>VIR Number:</Typography>
        </Grid>
        <Grid item xs={6} md={6} lg={6}>
          <Tooltip title={virNumberLabel} followCursor>
            <span>{truncateString(virNumberLabel, 15) || "-"}</span>
          </Tooltip>
        </Grid>
      </Grid>
      <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
        <Grid item xs={6} md={6} lg={6}>
          <Typography className={classes.readOnyTitle}>Container Number:</Typography>
        </Grid>
        <Grid item xs={6} md={6} lg={6}>
          <Tooltip title={containerNumbersLabel} followCursor>
            <span>{truncateString(containerNumbersLabel, 24) || "-"}</span>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );

  return (
    <div>
      <Paper
        elevation={1}
        variant="outlined"
        style={{ padding: "20px", backgroundColor: "rgb(0 0 0 / 7%)", border: "none" }}
      >
        <Grid spacing={2} container direction="row" justifyContent="space-between">
          {showVehicleStatus ? (
            <>
              {/* Vehicle Status Fields - shown when NOT in export mode */}
              {/* <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                <Grid item xs={6} md={6} lg={6}>
                  <Typography className={classes.readOnyTitle}>Vehicle Status ID:</Typography>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <Typography>{vehicleStatus?.vehicleStatusId || "-"}</Typography>
                </Grid>
              </Grid> */}
              <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                <Grid item xs={6} md={6} lg={6}>
                  <Typography className={classes.readOnyTitle}>Terminal Location:</Typography>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <Tooltip title={vehicleStatus?.terminalLocationName || "-"} followCursor>
                    <span>{truncateString(vehicleStatus?.terminalLocationName || "") || "-"}</span>
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                <Grid item xs={6} md={6} lg={6}>
                  <Typography className={classes.readOnyTitle}>Cargo Type:</Typography>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <Tooltip title={vehicleStatus?.cargoTypeName || "-"} followCursor>
                    <span>{truncateString(vehicleStatus?.cargoTypeName || "") || "-"}</span>
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                <Grid item xs={6} md={6} lg={6}>
                  <Typography className={classes.readOnyTitle}>Consignment Type:</Typography>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <Tooltip title={vehicleStatus?.consignmentTypeName || "-"} followCursor>
                    <span>{truncateString(vehicleStatus?.consignmentTypeName || "") || "-"}</span>
                  </Tooltip>
                </Grid>
              </Grid>
              {/* <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                <Grid item xs={6} md={6} lg={6}>
                  <Typography className={classes.readOnyTitle}>Container Number:</Typography>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <Tooltip title={vehicleStatus?.containerNumber || "-"} followCursor>
                    <span>{truncateString(vehicleStatus?.containerNumber || "") || "-"}</span>
                  </Tooltip>
                </Grid>
              </Grid> */}
              <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                <Grid item xs={6} md={6} lg={6}>
                  <Typography className={classes.readOnyTitle}>Date/Time:</Typography>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <Typography>
                    {vehicleStatus?.dateTime ? (
                      <Tooltip
                        title={
                          `${moment(vehicleStatus.dateTime).format("DD-MM-YYYY hh:mm a")}` || "-"
                        }
                        followCursor
                      >
                        <span>
                          {truncateString(
                            `${moment(vehicleStatus.dateTime).format("DD-MM-YYYY hh:mm a")}` || ""
                          ) || "-"}
                        </span>
                      </Tooltip>
                    ) : (
                      "-"
                    )}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                <Grid item xs={6} md={6} lg={6}>
                  <Typography className={classes.readOnyTitle}>Cargo Type:</Typography>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <Tooltip title={vehicleStatus?.cargoType || "-"} followCursor>
                    <span>{truncateString(vehicleStatus?.cargoType || "") || "-"}</span>
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                <Grid item xs={6} md={6} lg={6}>
                  <Typography className={classes.readOnyTitle}>Container Size:</Typography>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <Tooltip title={vehicleStatus?.containerSize || "-"} followCursor>
                    <span>{truncateString(vehicleStatus?.containerSize || "") || "-"}</span>
                  </Tooltip>
                </Grid>
              </Grid>
              {terminalType !== 4 && (
                <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                  <Grid item xs={6} md={6} lg={6}>
                    <Typography className={classes.readOnyTitle}>Container Type:</Typography>
                  </Grid>
                  <Grid item xs={6} md={6} lg={6}>
                    <Tooltip title={vehicleStatus?.containerType || "-"} followCursor>
                      <span>{truncateString(vehicleStatus?.containerType || "") || "-"}</span>
                    </Tooltip>
                  </Grid>
                </Grid>
              )}
              {/* <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                <Grid item xs={6} md={6} lg={6}>
                  <Typography className={classes.readOnyTitle}>Vehicle ID:</Typography>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <Typography>{vehicleStatus?.vehicleId || "-"}</Typography>
                </Grid>
              </Grid> */}
              <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                <Grid item xs={6} md={6} lg={6}>
                  <Typography className={classes.readOnyTitle}>Vehicle Type:</Typography>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <Tooltip title={vehicleStatus?.vehicleType || "-"} followCursor>
                    <span>{truncateString(vehicleStatus?.vehicleType || "") || "-"}</span>
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
                <Grid item xs={6} md={6} lg={6}>
                  <Typography className={classes.readOnyTitle}>Vehicle Weight:</Typography>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <Typography>
                    {vehicleStatus?.vehicleWeight
                      ? `${vehicleStatus.vehicleWeight} ${
                          vehicleStatus?.vehicleWeightUnit || ""
                        }`.trim()
                      : "-"}
                  </Typography>
                </Grid>
              </Grid>
            </>
          ) : null}
          {showContainerDetails && (
            <>
              {isBillGroupedLayout ? (
                <>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    container
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Grid item xs={6} md={6} lg={6}>
                      <Typography className={classes.readOnyTitle}>BL #:</Typography>
                    </Grid>
                    <Grid item xs={6} md={6} lg={6}>
                      <Tooltip title={details?.blNumber || "-"} followCursor>
                        <span>{truncateString(details?.blNumber || "") || "-"}</span>
                      </Tooltip>
                    </Grid>
                  </Grid>

                  {terminalType === 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Vessel:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Tooltip title={details?.vessel || "-"} followCursor>
                          <span>{truncateString(details?.vessel || "") || "-"}</span>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  )}

                  {terminalType === 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Voyage:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={details?.voyage || "-"} followCursor>
                            <span>{truncateString(details?.voyage || "") || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}

                  {terminalType === 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Forwarder:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={forwarderLabel || "-"} followCursor>
                            <span>{truncateString(forwarderLabel) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}

                  <Grid
                    item
                    xs={12}
                    md={4}
                    container
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Grid item xs={6} md={6} lg={6}>
                      <Typography className={classes.readOnyTitle}>Line:</Typography>
                    </Grid>
                    <Grid item xs={6} md={6} lg={6}>
                      <Typography>
                        <Tooltip title={details?.line || "-"} followCursor>
                          <span>{truncateString(details?.line || "") || "-"}</span>
                        </Tooltip>
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={4}
                    container
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Grid item xs={6} md={6} lg={6}>
                      <Typography className={classes.readOnyTitle}>GD #:</Typography>
                    </Grid>
                    <Grid item xs={6} md={6} lg={6}>
                      <Typography>
                        <Tooltip title={details?.gdNumber || "-"} followCursor>
                          <span>{truncateString(details?.gdNumber || "", 15) || "-"}</span>
                        </Tooltip>
                      </Typography>
                    </Grid>
                  </Grid>

                  {terminalType !== 4 && renderVirAndContainerFields()}

                  {terminalType !== 4 && (
                    <>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        container
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Grid item xs={6} md={6} lg={6}>
                          <Typography className={classes.readOnyTitle}>Billing CBM:</Typography>
                        </Grid>
                        <Grid item xs={6} md={6} lg={6}>
                          <Typography>
                            <Tooltip title={details?.billingCBM || "-"} followCursor>
                              <span>{details?.billingCBM || "-"}</span>
                            </Tooltip>
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        container
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Grid item xs={6} md={6} lg={6}>
                          <Typography className={classes.readOnyTitle}>Manifested CBM:</Typography>
                        </Grid>
                        <Grid item xs={6} md={6} lg={6}>
                          <Typography>
                            <Tooltip title={details?.manifestCBM || "-"} followCursor>
                              <span>{details?.manifestCBM || "-"}</span>
                            </Tooltip>
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        container
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Grid item xs={6} md={6} lg={6}>
                          <Typography className={classes.readOnyTitle}>Gate In Date:</Typography>
                        </Grid>
                        <Grid item xs={6} md={6} lg={6}>
                          <Typography>
                            {details?.gateInDate || details?.createdDateTime ? (
                              <Tooltip
                                title={
                                  `${moment(details?.gateInDate || details?.createdDateTime).format(
                                    "DD-MM-YYYY hh:mm a"
                                  )}` || "-"
                                }
                                followCursor
                              >
                                <span>
                                  {truncateString(
                                    `${moment(
                                      details?.gateInDate || details?.createdDateTime
                                    ).format("DD-MM-YYYY hh:mm a")}` || ""
                                  ) || "-"}
                                </span>
                              </Tooltip>
                            ) : (
                              "-"
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                      {!isPartywiseInvoice && (
                        <Grid
                          item
                          xs={12}
                          md={4}
                          container
                          direction="row"
                          justifyContent="space-between"
                        >
                          <Grid item xs={6} md={6} lg={6}>
                            <Typography className={classes.readOnyTitle}>Storage Days:</Typography>
                          </Grid>
                          <Grid item xs={6} md={6} lg={6}>
                            <Typography>
                              <Tooltip title={storageDays || 0} followCursor>
                                <span>{storageDays || 0}</span>
                              </Tooltip>
                            </Typography>
                          </Grid>
                        </Grid>
                      )}
                    </>
                  )}

                  {terminalType === 4 && !isPartywiseInvoice && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Total Indexes:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={details?.totalIndexes || "-"} followCursor>
                            <span>{truncateString(details?.totalIndexes || "") || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}

                  {terminalType === 4 && isBillGroupedLayout && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Index Number:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={bydSelectedIndexNumber || "-"} followCursor>
                            <span>{truncateString(bydSelectedIndexNumber || "") || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}

                  {terminalType === 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Container Count:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Tooltip title={bydContainerCountLabel || "-"} followCursor>
                          <span>{truncateString(bydContainerCountLabel || "", 12) || "-"}</span>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  )}

                  {/*
                  terminalType !== 4 — uncomment to restore Container Type (bill grouped layout)
                  {terminalType !== 4 && ( ... Container Type grid ... )}
                  */}

                  {isBillGroupedLayout && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Consignee:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Tooltip
                          title={
                            (details as any)?.consigneeName || (details as any)?.consignee || "-"
                          }
                          followCursor
                        >
                          <span>
                            {truncateString(
                              (details as any)?.consigneeName || (details as any)?.consignee || ""
                            ) || "-"}
                          </span>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  )}

                  {isPartywiseInvoice && terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Storage Days:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={partywiseStorageTooltip} followCursor>
                            <span>{displayPartywiseStorageDays || 0}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}

                  {isBillGroupedLayout && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Consignee NTN:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Tooltip title={(details as any)?.consigneeNtn || "-"} followCursor>
                          <span>{truncateString((details as any)?.consigneeNtn || "") || "-"}</span>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  )}

                  {terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Cargo Types:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={bydCargoTypesLabel || "-"} followCursor>
                            <span>{truncateString(bydCargoTypesLabel || "", 12) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Agent Name:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={agentNameLabel || "-"} followCursor>
                            <span>{truncateString(agentNameLabel || "", 12) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Agent CNIC:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={agentCnicLabel || "-"} followCursor>
                            <span>{truncateString(agentCnicLabel || "", 12) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>
                          Agent Phone Number:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={agentPhoneLabel || "-"} followCursor>
                            <span>{truncateString(agentPhoneLabel || "", 12) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Agent Rep Name:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={agentRepNameLabel || "-"} followCursor>
                            <span>{truncateString(agentRepNameLabel || "", 12) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </>
              ) : (
                <>
                  {/* Original Container Details Fields - hidden when in export mode */}
                  {terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>BL #:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Tooltip title={details?.blNumber || "-"} followCursor>
                          <span>{truncateString(details?.blNumber || "") || "-"}</span>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  )}
                  {terminalType === 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Forwarder:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={forwarderLabel || "-"} followCursor>
                            <span>{truncateString(forwarderLabel) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  <Grid
                    item
                    xs={12}
                    md={4}
                    container
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Grid item xs={6} md={6} lg={6}>
                      <Typography className={classes.readOnyTitle}>Line/Principal:</Typography>
                    </Grid>
                    <Grid item xs={6} md={6} lg={6}>
                      <Typography>
                        <Tooltip title={details?.line || "-"} followCursor>
                          <span>{truncateString(details?.line || "") || "-"}</span>
                        </Tooltip>
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              )}
              {!isBillGroupedLayout && (
                <>
                  {/* <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
            <Grid item xs={6} md={6} lg={6}>
              <Typography className={classes.readOnyTitle}>Client:</Typography>
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              <Typography>
                <Tooltip title={details?.client || "-"} followCursor>
                  <span>{truncateString(details?.client || "") || "-"}</span>
                </Tooltip>
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
            <Grid item xs={6} md={6} lg={6}>
              <Typography className={classes.readOnyTitle}>Line:</Typography>
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              <Typography>
                <Tooltip title={details?.line || "-"} followCursor>
                  <span>{truncateString(details?.line || "") || "-"}</span>
                </Tooltip>
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
            <Grid item xs={6} md={6} lg={6}>
              <Typography className={classes.readOnyTitle}>Consignee:</Typography>
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              <Typography>
                <Tooltip title={details?.consignee || "-"} followCursor>
                  <span>{truncateString(details?.consignee || "") || "-"}</span>
                </Tooltip>
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
            <Grid item xs={6} md={6} lg={6}>
              <Typography className={classes.readOnyTitle}>C&F Agent:</Typography>
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              <Typography>
                <Tooltip title={details?.cnfAgent || "-"} followCursor>
                  <span>{truncateString(details?.cnfAgent || "") || "-"}</span>
                </Tooltip>
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4} container direction="row" justifyContent="space-between">
            <Grid item xs={6} md={6} lg={6}>
              <Typography className={classes.readOnyTitle}>Agent:</Typography>
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              <Typography>
                <Tooltip title={details?.clearingAgent || "-"} followCursor>
                  <span>{truncateString(details?.clearingAgent || "") || "-"}</span>
                </Tooltip>
              </Typography>
            </Grid>
          </Grid> */}
                  {!isBillGroupedLayout && terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>GD #:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={details?.gdNumber || "-"} followCursor>
                            <span>{truncateString(details?.gdNumber || "", 15) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {terminalType !== 4 && renderVirAndContainerFields()}
                  {/*
                  terminalType !== 4 — uncomment to restore STax / Storage Start / Storage End
                  {!isBillGroupedLayout && terminalType !== 4 && (
                    <>
                      STax, Storage Start, Storage End grids were here originally
                    </>
                  )}
                  */}
                  {!isBillGroupedLayout && terminalType !== 4 && (
                    <>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        container
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Grid item xs={6} md={6} lg={6}>
                          <Typography className={classes.readOnyTitle}>Billing CBM:</Typography>
                        </Grid>
                        <Grid item xs={6} md={6} lg={6}>
                          <Typography>
                            <Tooltip title={details?.billingCBM || "-"} followCursor>
                              <span>{details?.billingCBM || "-"}</span>
                            </Tooltip>
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  )}
                  <Grid
                    item
                    xs={12}
                    md={4}
                    container
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Grid item xs={6} md={6} lg={6}>
                      <Typography className={classes.readOnyTitle}>Gate In Date:</Typography>
                    </Grid>
                    <Grid item xs={6} md={6} lg={6}>
                      <Typography>
                        {details?.gateInDate || details?.createdDateTime ? (
                          <Tooltip
                            title={
                              `${moment(details?.gateInDate || details?.createdDateTime).format(
                                "DD-MM-YYYY hh:mm a"
                              )}` || "-"
                            }
                            followCursor
                          >
                            <span>
                              {truncateString(
                                `${moment(details?.gateInDate || details?.createdDateTime).format(
                                  "DD-MM-YYYY hh:mm a"
                                )}` || ""
                              ) || "-"}
                            </span>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                  {terminalType === 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Cargo Type:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={cargoTypeLabel} followCursor>
                            <span>{truncateString(cargoTypeLabel || "") || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {/*
                  terminalType !== 4 — uncomment to restore Destuffing
                  {!isBillGroupedLayout && terminalType !== 4 && ( ... Destuffing grid ... )}
                  */}
                  {!isBillGroupedLayout && terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Storage Days:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={storageDays || 0} followCursor>
                            <span>{storageDays || 0}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {!isBillGroupedLayout && terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Consignee:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip
                            title={
                              (details as any)?.consignee || (details as any)?.consigneeName || "-"
                            }
                            followCursor
                          >
                            <span>
                              {truncateString(
                                (details as any)?.consignee || (details as any)?.consigneeName || ""
                              ) || "-"}
                            </span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {!isBillGroupedLayout && terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Consignee NTN:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={(details as any)?.consigneeNtn || "-"} followCursor>
                            <span>
                              {truncateString((details as any)?.consigneeNtn || "") || "-"}
                            </span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {/*
                  terminalType !== 4 — uncomment to restore Per CBM / Per Index / Demurrage / Fuel /
                  Inhand Indexes / Total Indexes / Tracking / Documentation Charges rows
                  Original blocks used: {!isBillGroupedLayout && terminalType !== 4 && ( ... )}
                  */}
                  {terminalType === 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Container Size:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={details?.containerSize || "-"} followCursor>
                            <span>{truncateString(details?.containerSize || "") || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {/*
                  terminalType !== 4 — uncomment to restore Container Type / Cargo Weight /
                  Terminal Handling Charges (non-bill grouped layout)
                  */}
                  {!isBillGroupedLayout && terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Cargo Types:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={details?.cargoType || "-"} followCursor>
                            <span>{truncateString(details?.cargoType || "") || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {!isBillGroupedLayout && terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Agent Name:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={agentNameLabel || "-"} followCursor>
                            <span>{truncateString(agentNameLabel || "", 12) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Agent CNIC:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={agentCnicLabel || "-"} followCursor>
                            <span>{truncateString(agentCnicLabel || "", 12) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>
                          Agent Phone Number:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={agentPhoneLabel || "-"} followCursor>
                            <span>{truncateString(agentPhoneLabel || "", 12) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {terminalType !== 4 && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      container
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography className={classes.readOnyTitle}>Agent Rep Name:</Typography>
                      </Grid>
                      <Grid item xs={6} md={6} lg={6}>
                        <Typography>
                          <Tooltip title={agentRepNameLabel || "-"} followCursor>
                            <span>{truncateString(agentRepNameLabel || "", 12) || "-"}</span>
                          </Tooltip>
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      </Paper>
    </div>
  );
}
